import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { gql } from "@apollo/client";
import type { SyncablePriceListRevenueDepartment } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { PriceListRevenueDepartment as RxPriceListRevenueDepartment } from "../collections/price-list-revenue-department";
import type { RxDBCollections } from "../provider";
import {
  SyncPriceListRevenueDepartmentPullDocument,
  type WholeSyncablePriceListRevenueDepartmentFragment,
} from "./__generated__/price-list-revenue-department.gql";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicatePriceListRevenueDepartment = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxPriceListRevenueDepartment>;
}) => {
  const [enabled] = useFeatureFlag<boolean>(featureFlags.billing.priceListRxdb);

  const pull = useMemo<
    | ReplicationPullConfig<WholeSyncablePriceListRevenueDepartmentFragment>
    | undefined
  >(() => {
    if (!enabled) return undefined;
    return {
      query: SyncPriceListRevenueDepartmentPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { hours: 1 },
    };
  }, [enabled]);

  return useGqlReplication<
    RxPriceListRevenueDepartment,
    SyncablePriceListRevenueDepartment,
    null
  >({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlPriceListRevenueDepartmentToRxPriceListRevenueDepartment,
    batchSize: 100,
    metadataLocalStorageKeySuffix: "price-list-revenue-department",
  });
};

export const mapGqlPriceListRevenueDepartmentToRxPriceListRevenueDepartment = (
  revenueDepartment: WholeSyncablePriceListRevenueDepartmentFragment,
): WithDeleted<RxPriceListRevenueDepartment> => ({
  id: revenueDepartment.id,
  name: revenueDepartment.name,
  meta: {
    createdAt: revenueDepartment.meta.createdAt,
    createdByUserId: revenueDepartment.meta.createdByUserId,
    updatedAt: revenueDepartment.meta.updatedAt,
    updatedByUserId: revenueDepartment.meta.updatedByUserId,
  },
  _deleted: revenueDepartment.meta.deleted,
});

gql`
  fragment WholeSyncablePriceListRevenueDepartment on SyncablePriceListRevenueDepartment {
    id
    name
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncPriceListRevenueDepartmentPull(
    $input: PullPriceListRevenueDepartmentInput!
  ) {
    pullPriceListRevenueDepartment(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncablePriceListRevenueDepartment
      }
    }
  }
`;
